import React from 'react';

/**
 * InsightsMarkdown — small, safe, no-dependency markdown renderer
 * scoped to the long-form article surfaces under /insights.
 *
 * Supports:
 *   - paragraphs (blank-line separated)
 *   - `## ` heading-2  → <h2 class="gpc-h3">
 *   - `### ` heading-3 → <h3>
 *   - `- ` bullet lists (groups consecutive lines)
 *   - **bold** inline
 *   - blockquote `> ...`
 *
 * Intentionally does NOT support raw HTML, scripts, links with `javascript:`,
 * or anything that could escape the article body. All text is rendered via
 * React text nodes (no `dangerouslySetInnerHTML`), so XSS surface is zero.
 */

const renderInline = (text, keyPrefix) => {
  // Split on **bold** while preserving order; everything else stays as text nodes.
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**') && part.length >= 4) {
      return (
        <strong key={`${keyPrefix}-b-${i}`} style={{ color: 'var(--gpc-ink)', fontWeight: 600 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
};

export const InsightsMarkdown = ({ source, testId = 'insight-body' }) => {
  if (!source || typeof source !== 'string') return null;
  const lines = source.replace(/\r\n/g, '\n').split('\n');

  // Group lines into block-level chunks: paragraphs, lists, headings, blockquotes.
  const blocks = [];
  let buffer = [];
  let listBuffer = [];

  const flushParagraph = () => {
    if (buffer.length) {
      blocks.push({ type: 'p', lines: buffer });
      buffer = [];
    }
  };
  const flushList = () => {
    if (listBuffer.length) {
      blocks.push({ type: 'ul', items: listBuffer });
      listBuffer = [];
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line === '') {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith('## ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'h2', text: line.slice(3).trim() });
      continue;
    }
    if (line.startsWith('### ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'h3', text: line.slice(4).trim() });
      continue;
    }
    if (line.startsWith('> ')) {
      flushParagraph();
      flushList();
      blocks.push({ type: 'quote', text: line.slice(2).trim() });
      continue;
    }
    if (line.startsWith('- ')) {
      flushParagraph();
      listBuffer.push(line.slice(2).trim());
      continue;
    }
    // Regular paragraph line — accumulate with soft-wrap joined by a space.
    flushList();
    buffer.push(line);
  }
  flushParagraph();
  flushList();

  return (
    <div data-testid={testId} className="gpc-insight-body">
      {blocks.map((b, i) => {
        if (b.type === 'h2') {
          return (
            <h2
              key={`h2-${i}`}
              style={{
                fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
                fontWeight: 500,
                fontSize: 'clamp(1.5rem, 2.4vw, 1.875rem)',
                color: 'var(--gpc-ink)',
                lineHeight: 1.2,
                marginTop: '2.5rem',
                marginBottom: '0.9rem',
                letterSpacing: '-0.01em',
              }}
            >
              {renderInline(b.text, `h2-${i}`)}
            </h2>
          );
        }
        if (b.type === 'h3') {
          return (
            <h3
              key={`h3-${i}`}
              style={{
                fontFamily: 'Space Grotesk, system-ui, sans-serif',
                fontWeight: 600,
                fontSize: '1.0625rem',
                color: 'var(--gpc-ink)',
                marginTop: '1.75rem',
                marginBottom: '0.6rem',
                letterSpacing: '-0.005em',
              }}
            >
              {renderInline(b.text, `h3-${i}`)}
            </h3>
          );
        }
        if (b.type === 'quote') {
          return (
            <blockquote
              key={`q-${i}`}
              style={{
                borderLeft: '2px solid var(--gpc-gold)',
                paddingLeft: '1rem',
                margin: '1.5rem 0',
                fontFamily: 'Fraunces, Georgia, serif',
                fontStyle: 'italic',
                fontSize: '1.0625rem',
                color: 'var(--gpc-ink-soft)',
                lineHeight: 1.6,
              }}
            >
              {renderInline(b.text, `q-${i}`)}
            </blockquote>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul
              key={`ul-${i}`}
              style={{
                listStyleType: 'disc',
                paddingLeft: '1.25rem',
                margin: '1rem 0',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: '1rem',
                color: 'var(--gpc-ink-soft)',
                lineHeight: 1.7,
              }}
            >
              {b.items.map((item, j) => (
                <li key={`li-${i}-${j}`} style={{ marginBottom: '0.4rem' }}>
                  {renderInline(item, `li-${i}-${j}`)}
                </li>
              ))}
            </ul>
          );
        }
        // paragraph
        return (
          <p
            key={`p-${i}`}
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: '1rem',
              color: 'var(--gpc-ink-soft)',
              lineHeight: 1.75,
              marginBottom: '1.1rem',
            }}
          >
            {renderInline(b.lines.join(' '), `p-${i}`)}
          </p>
        );
      })}
    </div>
  );
};

export default InsightsMarkdown;
